import React, { useEffect, useRef, useState } from "react";
import ZenkoLogo from "./ZenkoLogo";
import WhatsAppDemo from "./WhatsAppDemo";
import Origin from "./Origin";
import CaseStudy from "./CaseStudy";
import PoweredBy from "./PoweredBy";
import FloatingCTA from "./FloatingCTA";
import Cal, { getCalApi } from "@calcom/embed-react";
import { useLang } from "@/i18n/LangContext";
import {
  ArrowRight, ArrowUpRight, Sparkles, Bot, Globe, ShoppingBag, Zap, Clock,
  TrendingUp, CheckCircle2, MessageCircle, Search, PenLine, Rocket, Plus, Minus,
  Phone, Mail, Star,
} from "lucide-react";

/* ---------- Nav ---------- */
const Nav = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // Mount-only listener; setScrolled is stable per React docs, onScroll is scoped inside.
  }, []);
  const links = [
    { href: "#origen", label: t.nav.origen },
    { href: "#servicios", label: t.nav.servicios },
    { href: "#proceso", label: t.nav.proceso },
    { href: "#casos", label: t.nav.casos },
    { href: "#demo", label: t.nav.demo },
    { href: "#precios", label: t.nav.precios },
    { href: "#faq", label: t.nav.faq },
  ];
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "py-3" : "py-5"}`} data-testid="main-nav">
      <div className="mx-auto max-w-7xl px-5">
        <div className={`flex items-center justify-between rounded-full px-4 sm:px-5 py-2.5 transition-all ${
          scrolled ? "zk-glass shadow-2xl shadow-black/40" : "bg-white/[0.03] border border-white/5 backdrop-blur-xl"
        }`}>
          <a href="#top" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <ZenkoLogo size={32} />
            <span className="font-display font-extrabold text-xl tracking-tight">Zenko</span>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <a key={l.href} href={l.href} data-testid={`nav-link-${l.href.slice(1)}`}
                className="px-3.5 py-1.5 text-sm text-[#F5F7FA]/70 hover:text-white rounded-full hover:bg-white/5 transition-all">
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center bg-white/5 rounded-full p-0.5 border border-white/5">
              <button
                data-testid="lang-toggle-es"
                onClick={() => setLang("es")}
                aria-pressed={lang === "es"}
                className={`px-3 py-1 text-xs font-medium rounded-full transition ${
                  lang === "es" ? "bg-[#00E5A0] text-[#0D1B2A]" : "text-[#F5F7FA]/50 hover:text-white"
                }`}
              >
                {t.common.lang_es}
              </button>
              <button
                data-testid="lang-toggle-en"
                onClick={() => setLang("en")}
                aria-pressed={lang === "en"}
                className={`px-3 py-1 text-xs font-medium rounded-full transition ${
                  lang === "en" ? "bg-[#00E5A0] text-[#0D1B2A]" : "text-[#F5F7FA]/50 hover:text-white"
                }`}
              >
                {t.common.lang_en}
              </button>
            </div>
            <a href="#contacto" data-testid="nav-cta"
              className="zk-shine hidden md:inline-flex items-center gap-1.5 bg-[#00E5A0] text-[#0D1B2A] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#7CFFD0] transition-all">
              {t.nav.cta}
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}
              className="lg:hidden w-9 h-9 grid place-items-center rounded-full bg-white/5 border border-white/10" aria-label="Menu">
              <span className={`block w-4 h-0.5 bg-white transition ${open ? "rotate-45 translate-y-0.5" : ""}`} />
              <span className={`block w-4 h-0.5 bg-white mt-1 transition ${open ? "-rotate-45 -translate-y-0.5" : ""}`} />
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden mt-2 zk-glass rounded-3xl p-4 animate-fade-up">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                className="block px-3 py-2.5 text-sm text-[#F5F7FA]/80 hover:text-[#00E5A0] hover:bg-white/5 rounded-xl">
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 mt-2 px-3">
              <button onClick={() => setLang("es")} className={`flex-1 px-3 py-2 text-xs font-medium rounded-full ${lang === "es" ? "bg-[#00E5A0] text-[#0D1B2A]" : "bg-white/5 text-white"}`}>ES</button>
              <button onClick={() => setLang("en")} className={`flex-1 px-3 py-2 text-xs font-medium rounded-full ${lang === "en" ? "bg-[#00E5A0] text-[#0D1B2A]" : "bg-white/5 text-white"}`}>EN</button>
            </div>
            <a href="#contacto" onClick={() => setOpen(false)}
              className="mt-2 block text-center bg-[#00E5A0] text-[#0D1B2A] font-semibold py-2.5 rounded-full">
              {t.nav.cta} →
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

/* ---------- Hero ---------- */
const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  return (
    <section id="top" className="relative pt-32 lg:pt-40 pb-20 lg:pb-32 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 zk-grid pointer-events-none" />
      <div className="absolute top-20 -right-40 w-[600px] h-[600px] bg-[#00E5A0]/10 blur-[120px] rounded-full pointer-events-none animate-bg-shift" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#00E5A0]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#00E5A0]/10 border border-[#00E5A0]/25 rounded-full pl-2 pr-4 py-1.5 mb-7 animate-fade-up">
              <span className="bg-[#00E5A0] text-[#0D1B2A] text-[10px] font-bold px-2 py-0.5 rounded-full font-mono uppercase tracking-wider">{h.badge_new}</span>
              <span className="text-sm text-[#F5F7FA]/80">{h.badge_text}</span>
            </div>

            <h1 className="font-display font-extrabold text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.95] tracking-tight mb-7 animate-fade-up">
              {h.title_l1}{" "}
              <span className="relative inline-block">
                <span className="text-gradient-mint">{h.title_highlight}</span>
                <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none">
                  <path d="M2 9 Q 50 2 100 6 T 198 4" stroke="#00E5A0" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6" />
                </svg>
              </span>
              <br />
              {h.title_l2}<span className="text-[#00E5A0]">.</span>
            </h1>

            <p className="text-lg lg:text-xl text-[#F5F7FA]/70 max-w-xl mb-9 animate-fade-up" style={{ animationDelay: "0.1s" }}>
              {h.subtitle_a}<span className="text-white font-semibold">{h.subtitle_b}</span>{h.subtitle_c}
            </p>

            <div className="flex flex-wrap items-center gap-3 animate-fade-up" style={{ animationDelay: "0.2s" }}>
              <a href="#contacto" data-testid="hero-cta-primary"
                className="zk-shine group inline-flex items-center gap-2 bg-[#00E5A0] text-[#0D1B2A] font-semibold px-6 py-3.5 rounded-full hover:shadow-[0_20px_50px_-10px_rgba(0,229,160,0.5)] transition-all">
                {h.cta_primary}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#demo" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium px-6 py-3.5 rounded-full transition-all">
                <span className="w-2 h-2 rounded-full bg-[#00E5A0] animate-pulse" />
                {h.cta_secondary}
              </a>
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <HeroVisual />
          </div>
        </div>

        <div className="mt-20 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#6B7A8D] font-mono">
          <div className="w-12 h-px bg-[#6B7A8D]/40" />
          {h.scroll_cue}
        </div>
      </div>
    </section>
  );
};

const HeroVisual = () => {
  const { t } = useLang();
  const h = t.hero;
  return (
    <div className="relative aspect-square max-w-md mx-auto">
      <div className="absolute inset-0 rounded-full border border-[#00E5A0]/15 animate-orbit-slow" style={{ transformOrigin: "center" }}>
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#00E5A0] shadow-[0_0_20px_rgba(0,229,160,0.8)]" />
      </div>
      <div className="absolute inset-8 rounded-full border border-[#00E5A0]/10 animate-orbit-mid">
        <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 rounded-full bg-[#7CFFD0]" />
      </div>
      <div className="absolute inset-16 rounded-full border border-[#00E5A0]/8" />
      <div className="absolute inset-24 rounded-full border border-[#00E5A0]/5" />

      <div className="absolute top-4 -left-4 sm:-left-10 zk-glass rounded-2xl p-3 w-44 animate-float shadow-2xl shadow-black/30">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-7 h-7 rounded-lg bg-[#00E5A0]/20 grid place-items-center">
            <MessageCircle className="w-3.5 h-3.5 text-[#00E5A0]" />
          </div>
          <div>
            <div className="text-[10px] text-[#6B7A8D] font-mono uppercase tracking-wider">{h.card_wa}</div>
            <div className="text-xs font-semibold">{h.card_wa_title}</div>
          </div>
        </div>
        <div className="text-[10px] text-[#F5F7FA]/60">{h.card_wa_time}</div>
      </div>

      <div className="absolute top-1/2 -translate-y-1/2 -right-2 sm:-right-8 zk-glass rounded-2xl p-3 w-40 animate-float shadow-2xl shadow-black/30" style={{ animationDelay: "1s" }}>
        <div className="text-[10px] text-[#6B7A8D] font-mono uppercase tracking-wider mb-0.5">{h.card_sales}</div>
        <div className="font-display text-2xl font-extrabold text-white">$1,247<span className="text-sm text-[#00E5A0]">↑</span></div>
        <div className="text-[10px] text-[#00E5A0]">{h.card_sales_delta}</div>
      </div>

      <div className="absolute -bottom-2 left-4 zk-glass rounded-2xl p-3 w-52 animate-float shadow-2xl shadow-black/30" style={{ animationDelay: "0.5s" }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-[#00E5A0] animate-pulse" />
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#F5F7FA]/70">{h.card_typing}</div>
        </div>
        <div className="text-xs text-[#F5F7FA]/90 leading-snug">&ldquo;{h.card_typing_msg}&rdquo;</div>
      </div>

      <div className="absolute inset-0 grid place-items-center">
        <div className="relative">
          <div className="absolute inset-0 bg-[#00E5A0] blur-3xl opacity-30 rounded-full" />
          <div className="relative animate-pulse-mint rounded-full">
            <ZenkoLogo size={120} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Trust Ticker ---------- */
const TrustTicker = () => {
  const { t } = useLang();
  const all = [...t.trust.items, ...t.trust.items];
  return (
    <section className="py-10 border-y border-white/5 bg-[#0A1622] overflow-hidden" data-testid="trust-ticker">
      <div className="text-center text-xs uppercase tracking-[0.3em] text-[#6B7A8D] font-mono mb-6">
        {t.trust.label}
      </div>
      <div className="relative">
        <div className="flex gap-12 animate-ticker whitespace-nowrap">
          {all.map((name, i) => (
            <div key={i} className="flex items-center gap-3 text-[#F5F7FA]/50 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5A0]" />
              <span className="font-display font-semibold">{name}</span>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-[#0A1622] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-[#0A1622] to-transparent" />
      </div>
    </section>
  );
};

/* ---------- Counter + Stats ---------- */
const Counter = ({ end, duration = 1800, suffix = "" }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const step = (tm) => {
            const p = Math.min((tm - start) / duration, 1);
            setVal(Math.floor(end * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
    // `end` and `duration` are the only stable inputs; setVal is stable per React docs.
  }, [end, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const Stats = () => {
  const { t } = useLang();
  return (
    <section className="py-20 lg:py-28" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 rounded-3xl overflow-hidden border border-white/5">
          {t.stats.map((s, i) => (
            <div key={i} className="bg-[#0D1B2A] p-7 lg:p-9 hover:bg-[#122438] transition-colors">
              <div className="font-display text-5xl lg:text-6xl font-extrabold tracking-tight zk-num mb-2">
                {s.prefix}<Counter end={s.value} suffix={s.suffix} />
              </div>
              <div className="text-sm font-semibold text-white mb-1">{s.label}</div>
              <div className="text-xs text-[#6B7A8D] leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Process · Square tile grid ---------- */
const Process = () => {
  const { t } = useLang();
  const p = t.process;
  const icons = [Search, PenLine, Rocket];
  return (
    <section id="proceso" className="py-20 lg:py-28 relative" data-testid="process-section">
      <div className="max-w-6xl mx-auto px-5">
        <SectionHeader eyebrow={p.eyebrow}
          title={<>{p.title_a}<span className="text-[#00E5A0]">{p.title_h}</span>{p.title_b}</>}
          subtitle={p.subtitle} />

        {/* Square tiles in flow */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 max-w-4xl mx-auto">
          {p.steps.map((s, i) => {
            const Icon = icons[i];
            return (
              <div
                key={i}
                data-testid={`process-step-${i + 1}`}
                className="group relative"
              >
                <div className="relative sm:aspect-square zk-glass rounded-3xl p-5 lg:p-6 flex flex-col hover:border-[#00E5A0]/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                  {/* Background number */}
                  <div className="absolute -bottom-4 -right-2 font-display font-extrabold text-[7rem] leading-none text-[#00E5A0]/[0.04] select-none pointer-events-none">
                    {s.n}
                  </div>

                  {/* Top: icon + step pill */}
                  <div className="flex items-center justify-between mb-auto">
                    <div className="w-11 h-11 rounded-2xl bg-[#00E5A0]/15 grid place-items-center text-[#00E5A0] border border-[#00E5A0]/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-right">
                      <div className="font-display font-extrabold text-xl text-white/15 leading-none">{s.n}</div>
                      <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#00E5A0] mt-1.5">{s.time}</div>
                    </div>
                  </div>

                  {/* Middle: title + desc */}
                  <div className="relative mt-4">
                    <h3 className="font-display text-xl lg:text-2xl font-bold mb-2 leading-tight">{s.title}</h3>
                    <p className="text-[12px] lg:text-[13px] text-[#F5F7FA]/65 leading-snug line-clamp-3">{s.desc}</p>
                  </div>

                  {/* Bottom: deliverable */}
                  <div className="relative flex items-center gap-1.5 pt-3 mt-3 border-t border-white/5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#00E5A0] flex-shrink-0" />
                    <span className="text-[11px] text-[#F5F7FA]/80 truncate">{s.deliverable}</span>
                  </div>
                </div>

                {/* Connector dots between tiles */}
                {i < p.steps.length - 1 && (
                  <>
                    {/* Desktop horizontal */}
                    <div className="hidden sm:flex absolute top-1/2 -translate-y-1/2 left-full -ml-1 lg:-ml-1.5 items-center gap-0.5 z-10" aria-hidden="true">
                      <span className="w-1 h-1 rounded-full bg-[#00E5A0]/40" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00E5A0]/60" />
                      <span className="w-2 h-2 rounded-full bg-[#00E5A0]" />
                    </div>
                    {/* Mobile vertical */}
                    <div className="sm:hidden flex flex-col items-center gap-0.5 py-1.5" aria-hidden="true">
                      <span className="w-1 h-1 rounded-full bg-[#00E5A0]/40" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00E5A0]/60" />
                      <span className="w-2 h-2 rounded-full bg-[#00E5A0]" />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- Services ---------- */
const Services = () => {
  const { t } = useLang();
  const s = t.services;
  const [active, setActive] = useState(0);
  const icons = [Globe, Bot, ShoppingBag];
  const previewTypes = ["web", "bot", "shop"];
  return (
    <section id="servicios" className="py-24 lg:py-32 bg-[#0A1622] relative overflow-hidden" data-testid="services-section">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00E5A0]/5 blur-[140px] rounded-full" />
      <div className="relative max-w-7xl mx-auto px-5">
        <SectionHeader eyebrow={s.eyebrow}
          title={<>{s.title_a}<br/><span className="text-[#00E5A0]">{s.title_h}</span>{s.title_b}</>}
          subtitle={s.subtitle} />

        <div className="mt-12 flex flex-wrap gap-2 justify-center" role="tablist">
          {s.items.map((it, i) => {
            const Icon = icons[i];
            return (
              <button key={i} data-testid={`service-tab-${i}`} onClick={() => setActive(i)}
                className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                  active === i ? "bg-[#00E5A0] text-[#0D1B2A] shadow-lg shadow-[#00E5A0]/30"
                    : "bg-white/5 text-[#F5F7FA]/70 hover:text-white hover:bg-white/10 border border-white/5"
                }`}>
                <Icon className="w-4 h-4" />
                <span className="text-sm">{it.title}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-12 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
          <div key={active} className="animate-fade-up">
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#00E5A0] mb-4">
              0{active + 1} · {s.items[active].title}
            </div>
            <h3 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] mb-5">
              {s.items[active].tagline}
            </h3>
            <ul className="space-y-3 mb-8">
              {s.items[active].bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[#F5F7FA]/80">
                  <div className="mt-1 w-5 h-5 rounded-full bg-[#00E5A0]/15 grid place-items-center flex-shrink-0">
                    <CheckCircle2 className="w-3 h-3 text-[#00E5A0]" />
                  </div>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <a href="#contacto" data-testid={`service-cta-${active}`}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-[#00E5A0] hover:text-[#0D1B2A] text-white font-medium px-5 py-2.5 rounded-full border border-white/10 hover:border-transparent transition-all group">
              {s.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          <div key={`prev-${active}`} className="animate-fade-up">
            <ServicePreview type={previewTypes[active]} labels={s} />
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicePreview = ({ type, labels }) => {
  if (type === "web") {
    return (
      <div className="max-w-md mx-auto lg:mx-0 lg:ml-auto">
        <div
          data-testid="service-preview-web"
          className="block zk-glass rounded-2xl p-3 shadow-2xl shadow-black/40 group cursor-default transition-all"
        >
          <div className="flex items-center gap-1.5 mb-2 px-1.5">
            <span className="w-2 h-2 rounded-full bg-red-400/60" />
            <span className="w-2 h-2 rounded-full bg-yellow-400/60" />
            <span className="w-2 h-2 rounded-full bg-green-400/60" />
            <div className="ml-2 text-[9px] font-mono text-[#6B7A8D] truncate flex-1">
              tu-marca.com
            </div>
          </div>
          {/* Generic branded preview */}
          <div className="relative rounded-xl overflow-hidden aspect-[16/10] p-4"
            style={{ background: "linear-gradient(135deg, #0A1622 0%, #122438 100%)" }}
          >
            {/* Subtle grid */}
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage:
                "linear-gradient(rgba(0,229,160,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,160,0.06) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }} />
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#00E5A0]/15 blur-[60px] rounded-full" />

            <div className="relative flex flex-col h-full">
              {/* Mini nav */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-[#00E5A0]/30 border border-[#00E5A0]" />
                  <div className="font-display font-extrabold text-[10px] text-white">Tu Marca</div>
                </div>
                <div className="flex gap-1.5">
                  {["Inicio", "Servicios", "Contacto"].map((n, i) => (
                    <div key={i} className="text-[7px] font-mono uppercase tracking-wider text-white/50">{n}</div>
                  ))}
                </div>
              </div>

              {/* Hero */}
              <div className="mb-2">
                <div className="text-[7px] font-mono uppercase tracking-[0.2em] text-[#00E5A0] mb-1">tu propuesta única</div>
                <div className="font-display font-extrabold text-base leading-[1] text-white">
                  Grandes ideas,<br/>
                  <span className="text-[#00E5A0]">mejor ejecución</span>.
                </div>
              </div>

              {/* Skeleton cards */}
              <div className="grid grid-cols-3 gap-1.5 mt-auto">
                {[0.9, 0.65, 0.8].map((h, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded p-1.5">
                    <div className="w-3 h-3 rounded bg-[#00E5A0]/30 mb-1" />
                    <div className="h-0.5 rounded-full bg-white/30 mb-0.5" style={{ width: `${h * 100}%` }} />
                    <div className="h-0.5 rounded-full bg-white/15" style={{ width: "60%" }} />
                  </div>
                ))}
              </div>

              {/* Fake CTA */}
              <div className="mt-2 self-start bg-[#00E5A0] text-[#0D1B2A] text-[7px] font-bold px-2 py-0.5 rounded-full">
                RESERVAR AHORA →
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2.5 px-1.5">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5A0]" />
              <span className="text-[10px] text-[#F5F7FA]/70 font-mono">Diseño a medida · SEO óptimo</span>
            </div>
            <span className="text-[10px] text-[#00E5A0] font-medium">
              &lt; 1.5s ⚡
            </span>
          </div>
        </div>
      </div>
    );
  }
  if (type === "bot") {
    return (
      <div className="zk-glass rounded-3xl p-6 shadow-2xl shadow-black/40 space-y-3">
        <div className="text-xs font-mono uppercase tracking-wider text-[#00E5A0] mb-2">flow demo</div>
        {[
          { from: "user", text: "¿Tienen pizza margarita?" },
          { from: "bot", text: "¡Sí! 🍕 Mediana $8 · Familiar $14. ¿Cuál te llevo?" },
          { from: "user", text: "Familiar a domicilio" },
          { from: "bot", text: "Perfecto. Pídeme tu dirección y te confirmo tiempo + total" },
        ].map((m, i) => (
          <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${
              m.from === "user" ? "bg-[#005C4B] text-white rounded-br-sm" : "bg-[#1F2C24] text-white rounded-bl-sm"
            }`}>{m.text}</div>
          </div>
        ))}
        <div className="pt-3 border-t border-white/5 grid grid-cols-3 gap-2 text-center">
          {[{ l: "Reply", v: "< 2s" }, { l: "Languages", v: "ES / EN" }, { l: "Uptime", v: "24/7" }].map((s, i) => (
            <div key={i}>
              <div className="font-display font-extrabold text-lg text-[#00E5A0]">{s.v}</div>
              <div className="text-[9px] uppercase tracking-wider text-[#6B7A8D] font-mono">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="zk-glass rounded-3xl p-6 shadow-2xl shadow-black/40">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#6B7A8D] mb-1">Verde Market</div>
          <div className="font-display font-extrabold text-xl">Live store</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-[#6B7A8D]">Today</div>
          <div className="font-display font-extrabold text-2xl text-[#00E5A0]">$1,247</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {[
          { n: "Aguacate Hass", p: "$3.20", img: "#558B2F" },
          { n: "Tomate cherry", p: "$2.10", img: "#C62828" },
          { n: "Limón orgánico", p: "$1.80", img: "#C0CA33" },
          { n: "Albahaca fresca", p: "$1.50", img: "#33691E" },
        ].map((p, i) => (
          <div key={i} className="bg-[#0a1622] rounded-2xl p-3 border border-white/5">
            <div className="aspect-square rounded-lg mb-2" style={{ background: p.img }} />
            <div className="text-xs font-medium truncate">{p.n}</div>
            <div className="text-[#00E5A0] font-display font-bold text-sm">{p.p}</div>
          </div>
        ))}
      </div>
      <div className="bg-[#00E5A0]/10 border border-[#00E5A0]/30 rounded-2xl p-3 flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#00E5A0]" />
        <span className="text-xs text-[#F5F7FA]/90">
          Bot recovered <strong className="text-[#00E5A0]">$340</strong> in abandoned carts this week
        </span>
      </div>
    </div>
  );
};

/* ---------- Demo Section ---------- */
const DemoSection = () => {
  const { t } = useLang();
  const d = t.demo;
  const icons = [Clock, TrendingUp, Sparkles];
  return (
    <section id="demo" className="py-24 lg:py-36 relative overflow-hidden" data-testid="demo-section">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-[#00E5A0]/8 blur-[140px] rounded-full pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-16 items-center">
          <div>
            <SectionHeader align="left" eyebrow={d.eyebrow}
              title={<>{d.title_a}<br/><span className="text-[#00E5A0]">{d.title_h}</span></>}
              subtitle={d.subtitle} />
            <div className="mt-8 space-y-3">
              {d.bullets.map((b, i) => {
                const Icon = icons[i];
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#00E5A0]/10 grid place-items-center flex-shrink-0 border border-[#00E5A0]/20">
                      <Icon className="w-4 h-4 text-[#00E5A0]" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">{b.t}</div>
                      <div className="text-sm text-[#F5F7FA]/60">{b.d}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <WhatsAppDemo />
        </div>
      </div>
    </section>
  );
};

/* ---------- Before / After ---------- */
const BeforeAfter = () => {
  const { t } = useLang();
  const c = t.comparison;
  return (
    <section className="py-24 lg:py-32 bg-[#0A1622]" data-testid="before-after">
      <div className="max-w-5xl mx-auto px-5">
        <SectionHeader eyebrow={c.eyebrow}
          title={<>{c.title_a}<span className="text-[#00E5A0]">{c.title_h}</span>{c.title_b}</>}
          subtitle={c.subtitle} />

        <div className="mt-12 zk-glass rounded-3xl overflow-hidden border border-white/5">
          <div className="grid grid-cols-[1.4fr_1fr_1fr] px-4 sm:px-6 py-3 border-b border-white/5 bg-white/[0.02]">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B7A8D]">{c.col_metric}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B7A8D]">{c.col_before}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#00E5A0]">{c.col_after}</div>
          </div>
          {c.rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr] px-4 sm:px-6 py-4 sm:py-5 items-center border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition">
              <div className="text-sm font-medium text-white pr-2">{r.topic}</div>
              <div className="text-sm text-[#F5F7FA]/50 line-through">{r.before}</div>
              <div className="text-sm font-bold text-[#00E5A0] flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                {r.after}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Pricing ---------- */
const Pricing = () => {
  const { t } = useLang();
  const p = t.pricing;
  return (
    <section id="precios" className="py-24 lg:py-36 relative" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5">
        <div className="flex flex-col items-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#00E5A0]/10 border border-[#00E5A0]/30 rounded-full px-4 py-1.5 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-[#00E5A0]" />
            <span className="text-xs font-medium text-[#00E5A0]">{p.offer}</span>
          </div>
          <SectionHeader eyebrow={p.eyebrow}
            title={<>{p.title_a}<span className="text-[#00E5A0]">{p.title_h}</span></>}
            subtitle={p.subtitle} />
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {p.plans.map((plan, i) => (
            <div key={i} data-testid={`pricing-card-${i}`}
              className={`relative rounded-3xl p-7 lg:p-8 transition-all zk-tilt ${
                plan.featured ? "bg-gradient-to-br from-[#00E5A0]/15 to-[#122438] border-2 border-[#00E5A0]/40 shadow-[0_30px_80px_-30px_rgba(0,229,160,0.4)]" : "zk-glass"
              }`}>
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00E5A0] text-[#0D1B2A] text-[10px] font-bold px-3 py-1 rounded-full font-mono uppercase tracking-wider">
                  {p.popular}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7A8D] font-mono mb-3">{plan.tag}</div>
              <h3 className="font-display text-2xl font-extrabold mb-5">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-sm text-[#6B7A8D]">{p.from}</span>
                <span className="font-display font-extrabold text-5xl tracking-tight">${plan.price}</span>
                <span className="font-display font-extrabold text-2xl text-[#00E5A0] ml-0.5" aria-hidden="true">*</span>
              </div>
              <div className="text-xs text-[#6B7A8D] mb-1">{p.one_time}</div>
              <div className="text-[11px] text-[#00E5A0]/80 mb-7 font-mono">{p.maint}</div>

              <ul className="space-y-2.5 mb-8">
                {plan.perks.map((perk, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-[#F5F7FA]/85">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.featured ? "text-[#00E5A0]" : "text-[#00E5A0]/70"}`} />
                    {perk}
                  </li>
                ))}
              </ul>

              <a href="#contacto" data-testid={`pricing-cta-${i}`}
                className={`block w-full text-center font-semibold py-3 rounded-full transition-all ${
                  plan.featured ? "bg-[#00E5A0] text-[#0D1B2A] hover:bg-[#7CFFD0]"
                    : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                }`}>
                {p.start} {plan.name.split(" ")[0]}
              </a>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-[#6B7A8D]/80 mt-6 font-mono" data-testid="iva-note">
          {p.iva_note}
        </p>

        <p className="text-center text-sm text-[#6B7A8D] mt-6">
          {p.help_a}<a href="#contacto" className="text-[#00E5A0] hover:underline">{p.help_b}</a>
        </p>
      </div>
    </section>
  );
};

/* ---------- Testimonials · Square card grid ---------- */
const Testimonials = () => {
  const { t } = useLang();
  const ts = t.testimonials;
  const cs = t.case_study;

  // Array of testimonials - easily extendable. Add more objects here when you have new ones.
  const items = [
    {
      type: "real",
      quote: cs.quote,
      name: "MotoTuning C.A.",
      location: "Aragua · Venezuela",
      initials: "MT",
      gradient: "linear-gradient(135deg, #E8C547, #A88A1F)",
      site: "https://mototuningca.com",
      label: ts.real_label,
    },
    // Placeholder slots — replace when you have real testimonials
    { type: "placeholder" },
    { type: "cta" },
  ];

  return (
    <section className="py-20 lg:py-28 bg-[#0A1622] relative" data-testid="testimonials-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#00E5A0]/5 blur-[140px] rounded-full pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5">
        <SectionHeader eyebrow={ts.eyebrow}
          title={<>{ts.title_a}<br/><span className="text-[#00E5A0]">{ts.title_h}</span>{ts.title_b}</>} />

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {items.map((it, i) => {
            if (it.type === "real") {
              return (
                <div
                  key={i}
                  data-testid={`testimonial-card-${i}`}
                  className="group relative aspect-square zk-glass rounded-3xl p-6 lg:p-7 flex flex-col border-l-[3px] border-[#00E5A0] hover:border-[#00E5A0]/80 transition-all overflow-hidden"
                >
                  <div className="absolute -top-4 -right-2 font-display font-extrabold text-[8rem] leading-none text-[#00E5A0]/[0.07] select-none pointer-events-none">
                    &ldquo;
                  </div>
                  <div className="relative flex items-center gap-2 mb-3">
                    <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#00E5A0]">— {it.label}</span>
                    <div className="ml-auto flex items-center gap-0.5 text-[#00E5A0]">
                      {[1,2,3,4,5].map((s) => <Star key={s} className="w-3 h-3 fill-current" />)}
                    </div>
                  </div>
                  <p className="relative font-display text-[15px] lg:text-base text-white leading-snug font-medium flex-1 line-clamp-6">
                    &ldquo;{it.quote}&rdquo;
                  </p>
                  <div className="relative flex items-center justify-between gap-2 pt-4 mt-4 border-t border-white/5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className="w-10 h-10 rounded-full grid place-items-center font-display font-extrabold text-xs flex-shrink-0"
                        style={{ background: it.gradient, color: "#0D1B2A" }}
                      >
                        {it.initials}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-white text-sm truncate">{it.name}</div>
                        <div className="text-[11px] text-[#6B7A8D] truncate">{it.location}</div>
                      </div>
                    </div>
                    {it.site && (
                      <a
                        href={it.site}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`testimonial-link-${i}`}
                        aria-label={ts.visit_site}
                        className="w-8 h-8 rounded-full bg-[#00E5A0]/10 grid place-items-center text-[#00E5A0] hover:bg-[#00E5A0] hover:text-[#0D1B2A] transition flex-shrink-0"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              );
            }
            if (it.type === "placeholder") {
              return (
                <div
                  key={i}
                  data-testid={`testimonial-card-${i}`}
                  className="aspect-square rounded-3xl border-2 border-dashed border-white/10 hover:border-[#00E5A0]/30 bg-transparent p-6 flex flex-col items-center justify-center text-center transition-all group"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#00E5A0]/10 border border-[#00E5A0]/20 grid place-items-center mb-4 group-hover:scale-110 transition-transform">
                    <Plus className="w-5 h-5 text-[#00E5A0]" />
                  </div>
                  <div className="font-display font-bold text-white text-base mb-2">{ts.placeholder_title}</div>
                  <div className="text-xs text-[#6B7A8D] leading-relaxed max-w-[200px]">
                    {ts.placeholder_desc_a}<br/>{ts.placeholder_desc_b}
                  </div>
                </div>
              );
            }
            // CTA card
            return (
              <a
                key={i}
                href="#contacto"
                data-testid="testimonial-cta-card"
                className="group aspect-square rounded-3xl p-6 lg:p-7 flex flex-col justify-between bg-gradient-to-br from-[#00E5A0]/15 to-[#122438] border border-[#00E5A0]/30 hover:border-[#00E5A0]/60 hover:shadow-[0_20px_50px_-10px_rgba(0,229,160,0.3)] transition-all"
              >
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5A0] mb-3">{ts.cta_eyebrow}</div>
                  <h4 className="font-display font-extrabold text-xl lg:text-2xl text-white leading-tight">
                    {ts.cta_title}
                  </h4>
                </div>
                <div className="flex items-center justify-between gap-2 mt-4">
                  <span className="inline-flex items-center gap-1.5 bg-[#00E5A0] text-[#0D1B2A] font-bold text-sm px-4 py-2 rounded-full group-hover:gap-2.5 transition-all">
                    {ts.cta_action}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                  <div className="text-[#00E5A0] opacity-50 group-hover:opacity-100 transition-opacity">
                    <Sparkles className="w-5 h-5" />
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQ = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 lg:py-32" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5">
        <SectionHeader eyebrow={t.faq.eyebrow} title={t.faq.title} />
        <div className="mt-12 space-y-3">
          {t.faq.items.map((item, i) => (
            <div key={i} className={`zk-glass rounded-2xl overflow-hidden transition-all ${open === i ? "border-[#00E5A0]/40" : ""}`}>
              <button data-testid={`faq-item-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full px-5 py-5 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02] transition">
                <span className="font-display font-semibold text-white">{item.q}</span>
                <div className="w-8 h-8 rounded-full bg-[#00E5A0]/10 grid place-items-center flex-shrink-0 border border-[#00E5A0]/20">
                  {open === i ? <Minus className="w-4 h-4 text-[#00E5A0]" /> : <Plus className="w-4 h-4 text-[#00E5A0]" />}
                </div>
              </button>
              <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: open === i ? "240px" : "0" }}>
                <div className="px-5 pb-5 text-[#F5F7FA]/70 text-sm leading-relaxed">{item.a}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Final CTA con Cal.com ---------- */
const FinalCTA = () => {
  const { t } = useLang();
  const c = t.cta_section;
  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: "first-call-zenko" });
      cal("ui", {
        hideEventTypeDetails: true,
        theme: "dark",
        cssVarsPerTheme: {
          dark: {
            "cal-brand": "#00E5A0",
            "cal-bg": "#0D1B2A",
            "cal-bg-emphasis": "#122438",
            "cal-bg-muted": "#0A1622",
            "cal-border": "rgba(0, 229, 160, 0.15)",
            "cal-border-subtle": "rgba(255, 255, 255, 0.06)",
            "cal-text": "#F5F7FA",
            "cal-text-emphasis": "#FFFFFF",
            "cal-text-muted": "#6B7A8D",
          },
        },
        layout: "month_view",
      });
    })();
    // One-time Cal.com UI init on mount; getCalApi is a stable module import.
  }, []);

  return (
    <section id="contacto" className="py-24 lg:py-32 relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00E5A0]/5 to-transparent" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#00E5A0]/10 blur-[160px] rounded-full pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-5">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#00E5A0]/10 border border-[#00E5A0]/30 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#00E5A0] animate-pulse" />
            <span className="text-xs font-medium text-[#00E5A0]">{c.offer}</span>
          </div>
          <h2 className="font-display font-extrabold text-[clamp(2.5rem,6vw,4.5rem)] leading-[0.95] tracking-tight mb-5">
            {c.title_a}<span className="text-gradient-mint">{c.title_h}</span>{c.title_b}
          </h2>
          <p className="text-lg text-[#F5F7FA]/70 max-w-2xl mx-auto">{c.subtitle}</p>
        </div>

        <div className="relative max-w-5xl mx-auto" data-testid="calendar-embed">
          <div className="absolute -inset-2 bg-[#00E5A0]/15 blur-3xl rounded-[3rem] pointer-events-none" />
          <div className="relative zk-glass rounded-[2rem] p-2 sm:p-3 shadow-2xl shadow-black/50 border border-[#00E5A0]/20 overflow-hidden">
            <div className="w-full h-[1180px] sm:h-[920px] lg:h-[720px]">
              <Cal namespace="first-call-zenko" calLink="steicy-hernandez-tablante/first-call-zenko"
                style={{ width: "100%", height: "100%", borderRadius: "1.7rem" }}
                config={{ layout: "month_view", theme: "dark" }}
                data-testid="calcom-inline" />
            </div>
          </div>
          <p className="text-center text-xs text-[#6B7A8D] mt-4 font-mono">{c.hint}</p>
        </div>

        <div className="mt-12 text-center">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#6B7A8D] mb-5">{c.alt_label}</div>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <a href="https://wa.me/34697718231?text=Hola%20Zenko,%20quiero%20agendar%20una%20llamada" target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
              className="zk-shine inline-flex items-center gap-2 bg-[#00E5A0] text-[#0D1B2A] font-bold px-7 py-3.5 rounded-full hover:shadow-[0_20px_50px_-10px_rgba(0,229,160,0.6)] transition-all">
              <MessageCircle className="w-5 h-5" />
              {c.wa}
            </a>
            <a href="tel:+34697718231" data-testid="cta-call"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium px-7 py-3.5 rounded-full transition-all">
              <Phone className="w-5 h-5" />
              {c.phone}
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#F5F7FA]/55">
            {c.checks.map((s, i) => <span key={i}>{s}</span>)}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = () => {
  const { t } = useLang();
  const f = t.footer;
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-white/5 bg-[#0A1622] py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5">
        {/* Brand values · floating cards */}
        <div className="mb-12 pb-10 border-b border-white/5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {f.values.map((v, i) => (
              <div
                key={i}
                data-testid={`footer-value-${i}`}
                className="group relative zk-glass rounded-2xl p-4 lg:p-5 hover:border-[#00E5A0]/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                {/* Background kanji watermark */}
                <div className="absolute -bottom-3 -right-1 font-display font-extrabold text-5xl lg:text-6xl leading-none text-[#00E5A0]/[0.06] select-none pointer-events-none group-hover:text-[#00E5A0]/10 transition-colors">
                  {v.jp}
                </div>
                <div className="relative">
                  <div className="font-display font-extrabold text-2xl lg:text-3xl text-[#00E5A0] leading-none mb-2 group-hover:scale-110 origin-left transition-transform">
                    {v.jp}
                  </div>
                  <div className="font-display font-bold text-white text-sm mb-1">{v.label}</div>
                  <div className="text-[11px] text-[#6B7A8D] leading-snug">{v.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-[1.5fr_1fr_1fr_1fr] gap-10 mb-10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <ZenkoLogo size={32} />
              <span className="font-display font-extrabold text-xl">Zenko</span>
            </div>
            <p className="text-sm text-[#F5F7FA]/60 leading-relaxed max-w-xs">{f.tagline}</p>
          </div>
          {f.cols.map((c, i) => (
            <div key={i}>
              <div className="font-display font-bold text-sm mb-4">{c.t}</div>
              <ul className="space-y-2">
                {c.l.map((it, j) => (
                  <li key={j}><a href="#" className="text-sm text-[#F5F7FA]/60 hover:text-[#00E5A0] transition">{it}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
          <div className="text-xs text-[#6B7A8D] font-mono">{f.copy.replace("{year}", year)}</div>
          <div className="flex items-center gap-4 text-xs text-[#6B7A8D]">
            <a href="mailto:hola@zenko.io" className="hover:text-[#00E5A0] flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> hola@zenko.io
            </a>
            <a href="https://wa.me/34697718231" target="_blank" rel="noopener noreferrer" className="hover:text-[#00E5A0] flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> +34 697 718 231
            </a>
            <a
              href="https://www.instagram.com/zenko.it/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label={f.instagram}
              className="group flex items-center gap-1.5 hover:text-[#00E5A0] transition"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform">
                <rect x="2" y="2" width="20" height="20" rx="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
              @zenko.it
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ---------- Floating WhatsApp ---------- */
const FloatingWA = () => {
  const { t } = useLang();
  return (
    <a href="https://wa.me/34697718231" target="_blank" rel="noopener noreferrer" data-testid="floating-whatsapp"
      className="fixed bottom-6 left-6 z-40 group" aria-label="WhatsApp">
      <div className="absolute inset-0 bg-[#00E5A0] rounded-full animate-pulse-mint" />
      <div className="relative w-14 h-14 rounded-full bg-[#00E5A0] grid place-items-center shadow-[0_15px_40px_-5px_rgba(0,229,160,0.6)] group-hover:scale-110 transition-transform">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="#0D1B2A">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </div>
      <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-white text-[#0D1B2A] text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-xl pointer-events-none">
        {t.footer.wa_hover}
      </div>
    </a>
  );
};

/* ---------- Section Header ---------- */
const SectionHeader = ({ eyebrow, title, subtitle, align = "center" }) => (
  <div className={align === "center" ? "text-center max-w-2xl mx-auto" : "max-w-xl"}>
    {eyebrow && (
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5A0] mb-4">
        — {eyebrow}
      </div>
    )}
    <h2 className="font-display font-extrabold text-[clamp(2rem,4.5vw,3.4rem)] leading-[1.05] tracking-tight mb-5">
      {title}
    </h2>
    {subtitle && <p className="text-lg text-[#F5F7FA]/65">{subtitle}</p>}
  </div>
);

/* ---------- Main ---------- */
const ZenkoLanding = () => {
  return (
    <div className="min-h-screen bg-[#0D1B2A] text-[#F5F7FA] overflow-x-hidden" data-testid="zenko-landing">
      <Nav />
      <Hero />
      <TrustTicker />
      <Stats />
      <Origin />
      <PoweredBy />
      <Process />
      <Services />
      <DemoSection />
      <BeforeAfter />
      <CaseStudy />
      <Pricing />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
      <FloatingWA />
      <FloatingCTA />
    </div>
  );
};

export default ZenkoLanding;
